import { SetStateAction, useEffect, useState, useCallback } from "react";
import { Input, Button, Tooltip, Table, Space, Popconfirm, Tag } from "antd";
import { Trash, Edit2, Refresh2 } from "iconsax-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import PageHeader from "../../../components/PageHeader";

import {
  Container,
  Main,
  SearchBar,
  Content,
  HeaderContent,
  ActionsWrapper,
  TableWrapper,
} from "./styles";
import { useProfiles, DataTypeProfile } from "../../../hooks/profile";
import { useAuth } from "../../../hooks/auth";
import Footer from "../../../components/Footer";
import Breadcrumb from "../../../components/Breadcrumb";

function ProfileSelect() {
  const navigate = useNavigate();
  const { getAll, getProfiles, deleteProfile } = useProfiles();
  const [busca, setBusca] = useState("");
  const [data, setData] = useState<DataTypeProfile[]>([]);
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      if (!user?.uid) return;
      try {
        await getAll(user.uid);
        setLoading(false);
      } catch (error) {
        navigate("/login");
        setLoading(false);
      }
    }
    loadData();
  }, [getAll, navigate, user?.uid]);

  // =================coisas da tabela=================

  const handleGetProfiles = useCallback(async () => {
    Swal.fire({
      title: "Carregando...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: async () => {
        Swal.showLoading();
        setLoading(true);
        try {
          const listagemDeProfissionais = await getProfiles();
          setData(listagemDeProfissionais);
          setLoading(false);
          Swal.close();
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Ocorreu um erro ao listar os perfis",
            text: "Tente novamente mais tarde",
            confirmButtonText: "Tentar novamente",
            confirmButtonColor: "var(--color-primary-light)",
          });
          setLoading(false);
          console.log(error);
        }
      },
    });
  }, [getProfiles]);

  const profilesWithSearch = data.filter(
    (current: DataTypeProfile): boolean => {
      return current.name.toUpperCase().includes(busca.toLocaleUpperCase());
    }
  );

  async function handleDeleteProfile(uid: string) {
    await deleteProfile(uid);
    handleGetProfiles();
  }

  useEffect(() => {
    handleGetProfiles();
  }, [handleGetProfiles]);

  function redirect(record: DataTypeProfile) {
    navigate(`/edit-profile/`, {
      state: { id: record.uid },
    });
  }

  const columns = [
    {
      title: "Nome do profissional",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Instituição Vínculada",
      dataIndex: "institution",
      key: "institution",
    },
    {
      title: "Status do usuário",
      dataIndex: "active",
      render: (active: any) => {
        const badgeColor = active ? "green" : "red";
        const badgeText = active ? "Ativo" : "Inativo";

        return (
          <Space size="middle">
            <Tag color={badgeColor}>{badgeText}</Tag>
          </Space>
        );
      },
    },
    {
      title: "Ações",
      dataIndex: "",
      key: "x",
      render: (record: DataTypeProfile) => {
        return (
          <Space size="middle">
            <Button
              shape="circle"
              type="primary"
              onClick={() => redirect(record)}
            >
              <Edit2 size={16} />
            </Button>
            <Popconfirm
              title="Deseja realmente apagar?"
              okText="Sim"
              cancelText="Não"
              onConfirm={() => handleDeleteProfile(record.uid)}
            >
              <Button shape="circle" danger>
                <Trash size={16} />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // ======================================================

  return (
    <Container id="page">
      <PageHeader
        title="Gerenciamento de Profissionais."
        description="Estes são todos os profissionais cadastrados no Bilíngua"
        type="manager"
      />
      <Main>
        <Breadcrumb
          items={[{ label: "Gerenciamento de Profissionais", active: true }]}
        />
        <SearchBar className="search">
          <Input
            placeholder="Digite um termo para buscar"
            type="text"
            size="large"
            allowClear
            value={busca}
            onChange={(e: { target: { value: SetStateAction<string> } }) => {
              setBusca(e.target.value);
            }}
            style={{ height: "6rem", marginRight: "2rem" }}
          />
          <Button
            type="primary"
            htmlType="button"
            style={{
              border: "0",
              backgroundColor: "var(--color-secondary-dark)",
              width: "15rem",
              height: "5rem",
              borderRadius: "0.8rem",
            }}
          >
            Buscar
          </Button>
        </SearchBar>

        <Content className="content" style={{ padding: 30 }}>
          <HeaderContent className="headerContent">
            Lista de profissionais cadastrados
            <span />
            <br />
            <ActionsWrapper className="itens">
              <Tooltip title="Cadastre um Novo Profissional">
                <Button
                  type="primary"
                  htmlType="button"
                  style={{
                    border: "0",
                    backgroundColor: "var(--color-secondary-dark)",
                    borderRadius: "0.8rem",
                  }}
                  onClick={() => navigate("/add-profile")}
                >
                  + Adicionar novo profissional
                </Button>
              </Tooltip>
              <Button
                onClick={() => handleGetProfiles()}
                icon={<Refresh2 size={18} />}
                loading={loading}
              />
            </ActionsWrapper>
          </HeaderContent>

          <TableWrapper className="table">
            <Table
              rowSelection={{
                type: "checkbox",
                onSelect: (record) => {
                  console.log(record);
                },
              }}
              columns={columns}
              dataSource={profilesWithSearch}
              pagination={{ hideOnSinglePage: true }}
              rowKey="uid"
            />
          </TableWrapper>
        </Content>
      </Main>
      <Footer />
    </Container>
  );
}

export default ProfileSelect;
